import { Component, LOCALE_ID, OnInit, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { links } from '../../shared/links';
import { AmortisationCalculatorComponent } from './calculator/calculator.component';
import { FAQ_ENTRIES, buildFaqJsonLd } from './core/faq.data';

/** Lokalisierte Routen-Segmente – identisch mit app.routes.ts. */
export const RECHNER_ROUTES = {
  rechner: $localize`:@@routes.rechner:rechner`,
  amortisation: $localize`:@@routes.rechner.item.amortisation:amortisation`,
};

/**
 * Landingpage /rechner/amortisation.
 *
 * Zielt auf das Kosten- und Rentabilitäts-Cluster der Search Console:
 * "was kostet ein verkaufsautomat", "automaten aufstellen kosten",
 * "verkaufsautomat mieten", "wie rentabel sind automaten", "umsatz verkaufsautomat".
 * Dort standen rund 52.000 Impressionen bei unter 1 % CTR, weil keine passende
 * Seite existierte.
 */
@Component({
  selector: 'app-rechner-amortisation-page',
  standalone: true,
  imports: [RouterModule, AmortisationCalculatorComponent],
  templateUrl: './rechner-amortisation-page.component.html',
  styleUrl: './rechner-amortisation-page.component.css',
})
export class RechnerAmortisationPageComponent implements OnInit {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly locale = inject(LOCALE_ID);

  readonly faqs = FAQ_ENTRIES;
  readonly links = links;
  readonly guideLocationPath = $localize`:@@routes.guide.item.location:standortguide`;
  readonly guideFundingPath = $localize`:@@routes.guide.item.funding:foerdermittel`;

  ngOnInit(): void {
    this.title.setTitle(
      $localize`:@@rechnerPage.meta.title:Was kostet ein Verkaufsautomat? Preise & Amortisation`,
    );

    this.meta.updateTag({
      name: 'description',
      content: $localize`:@@rechnerPage.meta.description:Was kostet ein Verkaufsautomat? Kauf ab 6.800 € oder Miete ab 300 €/Monat, ~92 € laufende Kosten – im Schnitt 2.076 € Umsatz, Break-even nach 7,5 Monaten.`,
    });
    this.meta.updateTag({
      property: 'og:title',
      content: $localize`:@@rechnerPage.meta.ogTitle:Was kostet ein Verkaufsautomat – und ab wann rechnet er sich?`,
    });

    const canonical = this.canonicalUrl();
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.setCanonical(canonical);
    this.injectFaqSchema();
  }

  /**
   * In-Page-Anker. Plain href="#id" scheitert an <base href="/">: Der Browser
   * navigiert zu "/#id" statt zu scrollen. Deshalb explizit – gleiches Muster
   * wie in der Top-Nav.
   */
  scrollToAnchor(event: Event, id: string): void {
    event.preventDefault();
    // 'auto' statt 'smooth': Die Seite ist sehr lang, und smooth-Scrolling wird
    // durch nachladende Inhalte/Scroll-Listener abgebrochen und landet zu kurz.
    this.document.getElementById(id)?.scrollIntoView({
      behavior: 'auto',
      block: 'start',
      inline: 'nearest',
    });
  }

  private canonicalUrl(): string {
    const localePrefix = this.locale.startsWith('da') ? 'da' : this.locale.startsWith('nl') ? 'nl' : 'de';
    return `https://marktbox.de/${localePrefix}/${RECHNER_ROUTES.rechner}/${RECHNER_ROUTES.amortisation}`;
  }

  private setCanonical(href: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel=canonical]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = href;
  }

  /**
   * FAQPage-Schema. Google zeigt seit Mai 2026 keine FAQ-Rich-Results mehr an,
   * liest die Auszeichnung aber weiterhin zum Verständnis der Seite – und
   * KI-Suchsysteme greifen darauf zurück. Deshalb bleibt sie drin.
   */
  private injectFaqSchema(): void {
    const id = 'mb-faq-schema';
    this.document.getElementById(id)?.remove();

    const script = this.document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(buildFaqJsonLd(FAQ_ENTRIES));
    this.document.head.appendChild(script);
  }
}
